/**
 * ConversationVoiceRecorder — l'enregistreur d'un vocal : le moteur (`engine/voice/useVoiceRecorder`),
 * la réécoute (`useVoicePlayer`) et la feuille du design system (`<VoiceRecorderSheet>`). Celui d'un
 * message de la conversation et du chat du live (le bilan du tuteur, lui, enregistre dans sa carte :
 * `useTutorCloseOutRecorder`) ; un appelant peut lui donner un titre et une durée minimale.
 *
 * Monté seulement le temps que la feuille est ouverte : l'enregistreur n'existe pas hors du geste,
 * comme `AudioRecordingModal`, et le compteur qui court ne redessine que la feuille. « Envoyer »
 * remet le vocal à l'appelant (`onSend`) et referme ; la croix abandonne. Le micro refusé pour de bon
 * ouvre les réglages de l'appareil (mobile) ; ailleurs, on redemande.
 */
import React, { useCallback } from "react"
import { Linking, Platform } from "react-native"

import { VoiceRecorderSheet } from "../../../../../../design-system/student"
import { useVoiceRecorder } from "../../../../../../engine/voice/useVoiceRecorder"
import { type VoiceFile } from "../../../../../../engine/voice/voiceOutbox"
import { useVoicePlayer } from "../../../hooks/useVoicePlayer"

export type ConversationVoiceRecorderProps = {
    /** Le fil de la conversation : la réécoute s'arrête si l'on en change. */
    threadId?: string | null
    /** Le titre de la feuille ; défaut : celui de la feuille (« Message vocal »). */
    title?: string
    /** Une fin demandée avant cette durée se refuse ; défaut : aucune. */
    minDurationMs?: number
    /** Ce que la feuille dit d'un vocal trop court ; défaut : celui de la feuille. */
    tooShortMessage?: string
    onClose: () => void
    /** Le vocal fini, prêt à partir. */
    onSend: (file: VoiceFile) => void
}

const DRAFT_ID = "voice-draft"

export function ConversationVoiceRecorder(props: ConversationVoiceRecorderProps) {
    const { threadId = null, title, minDurationMs, tooShortMessage, onClose, onSend } = props
    const recorder = useVoiceRecorder({ minDurationMs })
    const preview = useVoicePlayer(threadId)

    const close = useCallback(() => {
        preview.stop()
        void recorder.reset()
        onClose()
    }, [preview, recorder, onClose])

    const redo = useCallback(() => {
        preview.stop()
        void recorder.start()
    }, [preview, recorder])

    const send = useCallback(() => {
        if (recorder.uri == null || recorder.mimeType == null || recorder.extension == null) return
        preview.stop()
        onSend({
            uri: recorder.uri,
            mimeType: recorder.mimeType,
            extension: recorder.extension,
            durationMs: recorder.durationMs,
        })
        void recorder.reset()
        onClose()
    }, [preview, recorder, onSend, onClose])

    const settings = recorder.state === "denied" && !recorder.canAskAgain && Platform.OS !== "web"
    const draftUri = recorder.uri

    return (
        <VoiceRecorderSheet
            visible
            title={title}
            onClose={close}
            phase={recorder.state}
            durationMs={recorder.durationMs}
            runningSince={recorder.runningSince}
            maxDurationMs={recorder.maxDurationMs}
            notice={recorder.notice}
            tooShortMessage={tooShortMessage}
            onStart={() => void recorder.start()}
            onPause={recorder.pause}
            onResume={recorder.resume}
            onStop={() => void recorder.stop()}
            stopping={recorder.stopping}
            playback={
                draftUri != null
                    ? {
                          playing: preview.messageId === DRAFT_ID && preview.playing,
                          progress: preview.messageId === DRAFT_ID ? preview.progress : 0,
                          onToggle: () => preview.toggle(DRAFT_ID, draftUri),
                      }
                    : undefined
            }
            onRedo={redo}
            onSend={send}
            onOpenSettings={settings ? () => void Linking.openSettings() : undefined}
        />
    )
}
